import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  FaGlobe, FaSearch, FaShieldAlt, FaBook, FaTools, FaExclamationTriangle, 
  FaUsers, FaLock, FaSignOutAlt, FaMicrophone, FaChevronLeft, FaBars, 
  FaChevronRight, FaTimes, FaLanguage, FaArrowRight 
} from 'react-icons/fa';
import { Button } from "@/components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from "framer-motion";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOnboardingHighlight } from "@/contexts/OnboardingHighlightContext";

// Define navigation links based on translations
const getNavLinks = (t: (key: string) => string) => [
  { to: "/dashboard", label: t("dashboard"), icon: <FaGlobe /> },
  { to: "/detect", label: t("analyzeContent"), icon: <FaSearch /> },
  { to: "/sources", label: t("sourceCredibility"), icon: <FaShieldAlt /> },
  { to: "/report", label: t("reportMisinfo"), icon: <FaExclamationTriangle /> },
  { to: "/learn", label: t("mediaLiteracy"), icon: <FaBook /> },
  { to: "/tools", label: t("osintTools"), icon: <FaTools /> },
  { to: "/community", label: t("community"), icon: <FaUsers /> },
  { to: "/privacy-and-hacking", label: t("privacySecurity"), icon: <FaLock /> },
  { to: "/cyber-threat", label: "Cyber Threat", icon: <FaShieldAlt /> },
  { to: "/voice-agent", label: t("voiceAgent"), icon: <FaMicrophone /> },
];

export default function EnhancedSidebar() {
  // Use the mobile menu context to get/set the mobile menu state
  const { isMobileMenuOpen, setMobileMenuOpen, closeMobileMenu } = useMobileMenu();
  // For desktop/tablet view
  const [isOpen, setIsOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 640,
    isTablet: window.innerWidth >= 640 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  // Get language context
  const { language, setLanguage, t } = useLanguage();

  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { highlightedPath } = useOnboardingHighlight();

  // Get nav links with translated labels
  const navLinks = getNavLinks(t);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const wasMobile = screenSize.isMobile;
      const newScreenSize = {
        width: width,
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024
      };
      
      setScreenSize(newScreenSize);
      
      // When switching from mobile to desktop/tablet, show the sidebar
      if (wasMobile && !newScreenSize.isMobile) {
        setIsOpen(true);
      }
      // Auto-collapse sidebar on mobile
      else if (newScreenSize.isMobile) {
        // Don't change isOpen here - it will be controlled by mobile menu context
        setIsCollapsed(false); // No collapsed state on mobile, just open/closed
      } else if (newScreenSize.isTablet && !isCollapsed) {
        setIsOpen(true); 
        setIsCollapsed(true); // Default to collapsed on tablet
      } else if (newScreenSize.isDesktop) {
        // Always show sidebar on desktop
        setIsOpen(true);
        // Optionally expand on desktop
        if (isCollapsed) {
          setIsCollapsed(false);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);
  
  // Use the mobile menu state for controlling sidebar visibility on mobile
  useEffect(() => {
    if (screenSize.isMobile) {
      setIsOpen(isMobileMenuOpen);
    }
  }, [isMobileMenuOpen, screenSize.isMobile]);
  
  // Close sidebar when clicking a link on mobile
  const handleMobileLinkClick = () => {
    if (screenSize.isMobile) {
      closeMobileMenu();
    }
  };

  // Lock body scroll on mobile when sidebar is open
  useEffect(() => {
    if (screenSize.isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [screenSize.isMobile, isOpen]);

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const toggleSidebar = () => {
    if (screenSize.isMobile) {
      // On mobile, the "toggle" button is the X in the sidebar
      closeMobileMenu();
    } else {
      // On desktop/tablet, toggle expanded vs collapsed
      setIsCollapsed(!isCollapsed);
    }
  };

  // Language selector items with display names
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'pidgin', label: 'Pidgin English' },
  ];

  // Function to handle language change
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'fr' | 'pidgin');
  };

  return (
    <>
      {/* Mobile Overlay - Dark background when sidebar is open */}
      {screenSize.isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={closeMobileMenu}
          style={{ animation: 'fadeIn 0.3s' }}
        />
      )}

      {/* Main Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            key="sidebar"
            initial={{ x: screenSize.isMobile ? -280 : (isCollapsed ? -200 : 0) }}
            animate={{ x: 0 }}
            exit={{ x: screenSize.isMobile ? -280 : (isCollapsed ? -200 : 0) }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              duration: 0.3
            }}
            className={`fixed inset-y-0 left-0 z-50 flex flex-col
              ${isCollapsed ? 'w-20' : 'w-64'}
              ${screenSize.isMobile ? 'w-64' : ''}
              transition-all duration-300 ease-in-out`}
          >
            {/* Close/Collapse Button */}
            <button
              onClick={toggleSidebar}
              className={`absolute top-3 ${isCollapsed ? 'right-2' : 'right-4'} 
                text-gray-300 hover:text-white
                bg-gray-800/50 hover:bg-gray-700/50 rounded-full p-1
                transition-all duration-200 transform hover:scale-110
                backdrop-blur-sm border border-gray-700/50 shadow-lg`}
            >
              {screenSize.isMobile ? (
                <FaTimes className="h-4 w-4" />
              ) : (
                isCollapsed ? <FaChevronRight className="h-4 w-4" /> : <FaChevronLeft className="h-4 w-4" />
              )}
            </button>

            {/* Sidebar Content */}
            <div className="flex flex-col h-full bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 border-r border-gray-800 shadow-2xl overflow-hidden relative">
              {/* Glassmorphism overlay */}
              <div className="absolute inset-0 bg-cyan-500/5 backdrop-blur-[2px] pointer-events-none" />
              
              {/* Content */}
              <div className="flex flex-col h-full z-10 relative overflow-hidden">
                {/* User info if logged in */}
                {currentUser && (
                  <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} p-3 border-b border-cyan-500/20 bg-black/30 rounded-b-xl mb-2 transition-all duration-300`}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400/80 to-blue-500/80 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {currentUser.email?.[0]?.toUpperCase() || "U"}
                    </div>
                    {!isCollapsed && (
                      <div className="flex flex-col">
                        <span className="text-white font-semibold text-base truncate max-w-[120px]">{currentUser.email}</span>
                        <span className="text-xs text-cyan-300">Online</span>
                      </div>
                    )}
                  </div>
                )}

                {/* App Logo and Brand */}
                <div className={`border-b border-gray-800 flex items-center ${isCollapsed ? 'justify-center p-2' : 'gap-2 p-4'}`}>
                  <img src="/TRUTH NEXUS.svg" alt="Truth Nexus Logo" className={`${isCollapsed ? 'h-12 w-auto' : 'h-20 w-auto'}`} />
                  
                </div>

                {/* Navigation Links */}
                <nav className={`flex-1 overflow-y-auto custom-scrollbar ${isCollapsed ? 'p-2' : 'p-4'}`}>
                  {navLinks.map((link) => {
                    const isActive = location.pathname === link.to;
                    return (
                      <TooltipProvider key={link.to}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={link.to}
                              onClick={handleMobileLinkClick}
                              className={`
                                block w-full text-left py-3 transition-all duration-200 ease-in-out relative
                                group 
                                ${isCollapsed ? "px-0 flex items-center justify-center" : "px-4"}
                                ${ isActive
                                    ? "text-cyan-300 bg-cyan-500/10 border-r-4 border-cyan-400 font-semibold"
                                    : "text-gray-400 hover:text-cyan-300 hover:bg-cyan-500/5"
                                }
                                ${ link.to === highlightedPath && !isActive 
                                  ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-900 bg-cyan-500/15 text-cyan-200 shadow-lg scale-105 z-10" 
                                  : ""
                                }
                                ${ link.to === highlightedPath && isActive 
                                  ? "ring-2 ring-cyan-500 ring-offset-2 ring-offset-gray-900 scale-105 z-10 shadow-lg" 
                                  : ""
                                }
                              `}
                            >
                              <div className={`flex items-center ${isCollapsed ? "justify-center" : ""}`}>
                                <span className={`text-xl ${isCollapsed ? "" : "mr-4"}`}>{link.icon}</span>
                                {!isCollapsed && (
                                  <span className="truncate group-hover:text-shadow-cyan-sm transition-text-shadow duration-200">
                                    {link.label}
                                  </span>
                                )}
                              </div>
                              {link.to === highlightedPath && (
                                <motion.div 
                                  className="absolute -left-1 top-1/2 -translate-y-1/2 h-8 w-8 bg-cyan-400 rounded-full flex items-center justify-center opacity-80 shadow-2xl z-20"
                                  initial={{ scale: 0, x: isCollapsed? 0 : -10 }}
                                  animate={{ scale: 1, x: isCollapsed? 0 : -10 }}
                                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                                >
                                  <FaArrowRight className="text-black -rotate-45" />
                                </motion.div>
                              )}
                            </Link>
                          </TooltipTrigger>
                          {isCollapsed && (
                            <TooltipContent side="right" className="bg-black/90 border border-gray-800">
                              <p>{link.label}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </nav>

                {/* Language Selector */}
                <div className={`border-t border-gray-800 ${isCollapsed ? 'p-2' : 'p-4'}`}>
                  <DropdownMenu>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className={`w-full justify-start text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 ${isCollapsed ? "px-0 flex items-center justify-center" : "px-4"}`}
                            >
                              <FaLanguage className={isCollapsed ? "" : "mr-3"} />
                              {!isCollapsed && t("language")}
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right" className="bg-black/90 border border-gray-800">
                            <p>{t("language")}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <DropdownMenuContent className="min-w-[160px] bg-gray-900 border border-gray-800 shadow-xl">
                      {languageOptions.map((option) => (
                        <DropdownMenuItem 
                          key={option.value}
                          className={`
                            cursor-pointer px-4 py-2 flex items-center 
                            ${language === option.value ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300'} 
                            hover:bg-cyan-500/10 hover:text-cyan-200
                          `}
                          onClick={() => handleLanguageChange(option.value)}
                        >
                          {option.label}
                          {language === option.value && (
                            <span className="ml-auto">✓</span>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Sign Out Button */}
                <div className={`border-t border-gray-800 ${isCollapsed ? 'p-2' : 'p-4'}`}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-900/20 ${isCollapsed ? "px-0 flex items-center justify-center" : "px-4"} transition-all duration-300`}
                          onClick={handleSignOut}
                        >
                          <FaSignOutAlt className={isCollapsed ? "" : "mr-3"} />
                          {!isCollapsed && t("signOut")}
                        </Button>
                      </TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side="right" className="bg-black/90 border border-gray-800">
                          <p>{t("signOut")}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
